/**
 * PATH: packages/ui/src/mobile/useLongPress.ts
 * FILE_ID: UI-MOBILE-LONGPRESS-001
 * PURPOSE: Shared touch/mouse long-press → callback for mobile context actions.
 * WHY: Phone surfaces have no reliable contextmenu; long-press is the right-click
 *      twin. Lifted from job-hub schedule (500ms there) with a 1000ms default so
 *      quote-builder / CRM Excel-parity menus feel deliberate, not accidental.
 */
import { useCallback, useRef } from 'react'
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react'

const DEFAULT_LONG_PRESS_MS = 1000
const DEFAULT_MOVE_TOLERANCE_PX = 10
const CLICK_SUPPRESS_MS = 750
const INTERACTIVE_SELECTOR = 'input, textarea, select, button, a, [contenteditable="true"]'

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(INTERACTIVE_SELECTOR))
}

export interface UseLongPressOptions {
  /** Hold duration before firing. Default 1000ms. */
  delayMs?: number
  /** Cancel if the pointer moves more than this many px. Default 10. */
  moveTolerancePx?: number
  /**
   * When true (default), `onContextMenu` preventDefaults and fires the same
   * callback — desktop right-click stays additive alongside touch long-press.
   */
  enableContextMenu?: boolean
}

export interface LongPressBindHandlers {
  onTouchStart: (e: ReactTouchEvent) => void
  onTouchMove: (e: ReactTouchEvent) => void
  onTouchEnd: () => void
  onTouchCancel: () => void
  onMouseDown: (e: ReactMouseEvent) => void
  onMouseMove: (e: ReactMouseEvent) => void
  onMouseUp: () => void
  onMouseLeave: () => void
  onContextMenu: (e: ReactMouseEvent) => void
  onClickCapture: (e: ReactMouseEvent) => void
}

export function useLongPress<T>(
  onLongPress: (payload: T) => void,
  options: UseLongPressOptions = {},
) {
  const delayMs = options.delayMs ?? DEFAULT_LONG_PRESS_MS
  const moveTolerancePx = options.moveTolerancePx ?? DEFAULT_MOVE_TOLERANCE_PX
  const enableContextMenu = options.enableContextMenu !== false

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startRef = useRef<{ x: number; y: number; payload: T } | null>(null)
  const suppressClickUntil = useRef(0)
  const latest = useRef(onLongPress)
  latest.current = onLongPress

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    startRef.current = null
  }, [])

  const fire = useCallback((payload: T) => {
    latest.current(payload)
    suppressClickUntil.current = Date.now() + CLICK_SUPPRESS_MS
  }, [])

  const begin = useCallback(
    (clientX: number, clientY: number, payload: T) => {
      clear()
      startRef.current = { x: clientX, y: clientY, payload }
      timerRef.current = setTimeout(() => {
        const start = startRef.current
        if (!start) return
        fire(start.payload)
        clear()
      }, delayMs)
    },
    [clear, delayMs, fire],
  )

  const move = useCallback(
    (clientX: number, clientY: number) => {
      const start = startRef.current
      if (!start) return
      if (Math.hypot(clientX - start.x, clientY - start.y) > moveTolerancePx) {
        clear()
      }
    },
    [clear, moveTolerancePx],
  )

  const bind = useCallback(
    (payload: T): LongPressBindHandlers => ({
      onTouchStart: (e: ReactTouchEvent) => {
        if (isInteractiveTarget(e.target)) return
        const t = e.touches[0]
        if (!t) return
        begin(t.clientX, t.clientY, payload)
      },
      onTouchMove: (e: ReactTouchEvent) => {
        const t = e.touches[0]
        if (!t) return
        move(t.clientX, t.clientY)
      },
      onTouchEnd: clear,
      onTouchCancel: clear,
      onMouseDown: (e: ReactMouseEvent) => {
        if (e.button !== 0 || isInteractiveTarget(e.target)) return
        begin(e.clientX, e.clientY, payload)
      },
      onMouseMove: (e: ReactMouseEvent) => move(e.clientX, e.clientY),
      onMouseUp: clear,
      onMouseLeave: clear,
      onContextMenu: (e: ReactMouseEvent) => {
        if (!enableContextMenu || isInteractiveTarget(e.target)) return
        e.preventDefault()
        fire(payload)
      },
      onClickCapture: (e: ReactMouseEvent) => {
        if (Date.now() > suppressClickUntil.current) return
        e.preventDefault()
        e.stopPropagation()
        suppressClickUntil.current = 0
      },
    }),
    [begin, move, clear, enableContextMenu, fire],
  )

  return { bind, clear }
}
